import React from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { Campaign } from '../data/mockData';
ChartJS.register(ArcElement, Tooltip, Legend);
interface CampaignCardProps {
  campaign: Campaign;
}
export function CampaignCard({
  campaign
}: CampaignCardProps) {
  const chartData = {
    labels: campaign.channels.map(c => c.name),
    datasets: [{
      data: campaign.channels.map(c => c.value),
      backgroundColor: campaign.channels.map(c => c.color),
      borderWidth: 0,
      hoverOffset: 4
    }]
  };
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '70%',
    plugins: {
      legend: {
        position: 'right' as const,
        labels: {
          usePointStyle: true,
          boxWidth: 8,
          padding: 16,
          font: {
            family: 'system-ui, -apple-system, sans-serif',
            size: 12
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(15, 23, 42, 0.9)',
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          label: function (context: any) {
            return ` ${context.label}: ${context.raw}%`;
          }
        }
      }
    }
  };
  const accentColorClass = campaign.type === 'Brand' ? 'before:bg-slate-800' : 'before:bg-red-500';
  return <div className={`relative bg-card border border-border rounded-2xl p-6 pl-8 shadow-sm flex flex-col gap-6 before:absolute before:left-0 before:top-6 before:bottom-6 before:w-1.5 before:rounded-r-full ${accentColorClass} overflow-hidden`} data-id="element-11">
      {/* Header */}
      <div data-id="element-12">
        <div className="flex items-start justify-between mb-2" data-id="element-13">
          <h4 className="text-lg font-semibold text-foreground leading-tight" data-id="element-14">
            {campaign.title}
          </h4>
          <span className={`ml-3 px-3 py-1 rounded-full text-xs font-semibold shrink-0 ${campaign.type === 'Brand' ? 'bg-slate-800 text-white' : 'bg-red-500 text-white'}`} data-id="element-15">
            {campaign.type}
          </span>
        </div>
        <p className="text-sm text-muted-foreground" data-id="element-16">
          {campaign.date} • {campaign.model}
        </p>
      </div>

      {/* Metrics Grid */}
      <div className="flex flex-col gap-4" data-id="element-17">
        {/* Row 1: 4 columns */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-y-4 gap-x-4" data-id="element-18">
          <div className="flex flex-col" data-id="element-19">
            <span className="text-xs text-muted-foreground mb-1" data-id="element-20">Budget</span>
            <span className="text-sm font-semibold text-foreground" data-id="element-21">
              {campaign.metrics.budget}
            </span>
          </div>
          <div className="flex flex-col" data-id="element-22">
            <span className="text-xs text-muted-foreground mb-1" data-id="element-23">
              Impressions
            </span>
            <span className="text-sm font-semibold text-foreground" data-id="element-24">
              {campaign.metrics.impressions}
            </span>
          </div>
          <div className="flex flex-col" data-id="element-25">
            <span className="text-xs text-muted-foreground mb-1" data-id="element-26">Clicks</span>
            <span className="text-sm font-semibold text-foreground" data-id="element-27">
              {campaign.metrics.clicks}
            </span>
          </div>
          <div className="flex flex-col" data-id="element-28">
            <span className="text-xs text-muted-foreground mb-1" data-id="element-29">Leads</span>
            <span className="text-sm font-semibold text-foreground" data-id="element-30">
              {campaign.metrics.leads}
            </span>
          </div>
        </div>

        {/* Row 2: 3 columns */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-4 gap-x-4 pt-2 border-t border-border/50" data-id="element-31">
          <div className="flex flex-col" data-id="element-32">
            <span className="text-xs text-muted-foreground mb-1" data-id="element-33">
              Time on site
            </span>
            <span className="text-sm font-semibold text-foreground" data-id="element-34">
              {campaign.metrics.timeOnSite}
            </span>
          </div>
          <div className="flex flex-col" data-id="element-35">
            <span className="text-xs text-muted-foreground mb-1" data-id="element-36">
              Bounce rate
            </span>
            <span className="text-sm font-semibold text-foreground" data-id="element-37">
              {campaign.metrics.bounceRate}
            </span>
          </div>
          <div className="flex flex-col" data-id="element-38">
            <span className="text-xs text-muted-foreground mb-1" data-id="element-39">
              Cost per lead
            </span>
            <span className="text-sm font-semibold text-foreground" data-id="element-40">
              {campaign.metrics.costPerLead}
            </span>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="pt-4 border-t border-border mt-auto" data-id="element-41">
        <span className="text-xs font-medium text-muted-foreground block mb-4" data-id="element-42">
          Channel Distribution
        </span>
        <div className="h-[160px] w-full" data-id="element-43">
          <Doughnut data={chartData} options={chartOptions} data-id="element-44" />
        </div>
      </div>
    </div>;
}